import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { PlusIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Etiqueta } from "@/components/ui/etiqueta";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect, NativeSelectOption } from "@/components/ui/native-select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useClavesEditables, useEscribirHecho, useQuitarHecho } from "@/hooks/useGeneracion";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import {
  ETIQUETA_ESTADO_DE_HECHO,
  TONO_DE_ESTADO_DE_HECHO,
  clavesParaAgregar,
  type ClaveEditable,
  type Hecho,
} from "@/lib/generacion";

interface TablaDeHechosProps {
  informeId: number;
  hechos: Hecho[];
}

/** Lo que el diálogo edita: un hecho existente (clave fija) o uno nuevo. */
interface BorradorDeHecho {
  clave: string;
  sujeto: string;
  valor: string;
  /** Edición de un hecho que ya está: la clave y el sujeto no se cambian. */
  fijo: boolean;
}

/**
 * La hoja de hechos: lo que el redactor del informe puede citar, y qué
 * secciones lo citan hoy. «Lo citan» es lo que dice a qué se afecta si un
 * hecho cambia: esas secciones quedan desactualizadas.
 *
 * Los datos EXTRAÍBLES (los que no salen de una pestaña ni de un cálculo) los
 * puede escribir, confirmar, corregir y quitar una persona: lo escrito queda
 * firmado y ni el análisis ni la reconstrucción de la hoja lo pisan.
 */
export function TablaDeHechos({ informeId, hechos }: TablaDeHechosProps) {
  const claves = useClavesEditables(informeId).data ?? [];
  const escribir = useEscribirHecho(informeId);
  const quitar = useQuitarHecho(informeId);
  const [borrador, setBorrador] = useState<BorradorDeHecho | null>(null);
  const hayQueAgregar = clavesParaAgregar(claves, hechos).length > 0;

  function confirmar(h: Hecho) {
    escribir.mutate(
      { clave: h.clave, sujeto: h.sujeto_etiqueta ?? h.sujeto, valor: h.valor_formateado ?? "" },
      { onError: (error) => toast.error(getApiErrorMessage(error, "No pudimos confirmar el dato.")) },
    );
  }

  function quitarUno(h: Hecho) {
    quitar.mutate(
      { clave: h.clave, sujeto: h.sujeto_etiqueta ?? h.sujeto },
      {
        onSuccess: () => toast.success(`Quitamos «${h.nombre}» de la hoja.`),
        onError: (error) => toast.error(getApiErrorMessage(error, "No pudimos quitar el dato.")),
      },
    );
  }

  const agregar = hayQueAgregar ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => setBorrador({ clave: "", sujeto: "", valor: "", fijo: false })}
    >
      <PlusIcon aria-hidden /> Agregar dato
    </Button>
  ) : null;

  const dialogo = (
    <DialogoDeHecho
      informeId={informeId}
      claves={claves}
      hechos={hechos}
      borrador={borrador}
      onCerrar={() => setBorrador(null)}
    />
  );

  if (hechos.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3">
        <p className="text-meta text-ink-medio bg-card w-full rounded-[1.25rem] border-[1.5px] px-4 py-8 text-center">
          La hoja de hechos todavía no tiene hechos. Se arma al analizar el expediente.
        </p>
        {agregar}
        {dialogo}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-ink-muted max-w-3xl text-xs font-semibold text-pretty">
          <strong className="text-ink-body">Propuesto</strong>: lo leyó la IA de un documento y todavía nadie lo
          confirmó. Una respuesta a una duda lo deja <strong className="text-ink-body">Resuelto</strong>. Los datos
          leídos de un documento los puedes confirmar, corregir o quitar; lo que escribes queda firmado con tu nombre.
        </p>
        {agregar}
      </div>

      <div className="bg-card overflow-hidden rounded-[1.25rem] border-[1.5px]">
        <Table>
          <TableHeader className="bg-superficie-tenue">
            <TableRow>
              <TableHead variant="versalitas">Hecho</TableHead>
              <TableHead variant="versalitas">Valor</TableHead>
              <TableHead variant="versalitas">Fuente</TableHead>
              <TableHead variant="versalitas">Estado</TableHead>
              <TableHead variant="versalitas">Lo citan</TableHead>
              <TableHead variant="versalitas">
                <span className="sr-only">Acciones</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {hechos.map((h) => (
              <TableRow key={h.cita}>
                <TableCell className="text-[0.8125rem] font-bold">
                  {h.nombre}
                  {h.sujeto_etiqueta ? <span className="text-ink-hint font-normal"> · {h.sujeto_etiqueta}</span> : null}
                </TableCell>
                <TableCell className="font-mono text-[0.78125rem] font-bold">{h.valor_formateado ?? "—"}</TableCell>
                <TableCell className="text-ink-secundario text-[0.78125rem] font-semibold">
                  <div className="flex flex-col items-start gap-1">
                    {h.fuente.texto ?? "—"}
                    {h.fuente.tipo === "tasador" ? <Etiqueta tono="info">Escrito por una persona</Etiqueta> : null}
                    {h.fuente.tipo === "documento" ? <Etiqueta tono="neutral">Leído por la IA</Etiqueta> : null}
                  </div>
                </TableCell>
                <TableCell>
                  <Etiqueta tono={TONO_DE_ESTADO_DE_HECHO[h.estado]}>{ETIQUETA_ESTADO_DE_HECHO[h.estado]}</Etiqueta>
                </TableCell>
                <TableCell>
                  {h.citada_por.length === 0 ? (
                    <span aria-label="Ninguna sección lo cita" className="text-ink-hint">
                      —
                    </span>
                  ) : (
                    <ul className="flex flex-wrap gap-1">
                      {h.citada_por.map((s) => (
                        <li
                          key={s.item_id}
                          className="bg-divisor text-ink-secundario rounded-md px-2 py-0.5 text-[0.71875rem] font-semibold"
                        >
                          {s.nombre}
                        </li>
                      ))}
                    </ul>
                  )}
                </TableCell>
                <TableCell>
                  {h.origen === "extraible" ? (
                    <div className="flex flex-wrap justify-end gap-1">
                      {h.estado === "propuesto" ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          aria-label={`Confirmar ${h.nombre}`}
                          disabled={escribir.isPending}
                          onClick={() => confirmar(h)}
                        >
                          Confirmar
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label={`Editar ${h.nombre}`}
                        onClick={() =>
                          setBorrador({
                            clave: h.clave,
                            sujeto: h.sujeto_etiqueta ?? h.sujeto,
                            valor: h.valor_formateado ?? "",
                            fijo: true,
                          })
                        }
                      >
                        Editar
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        aria-label={`Quitar ${h.nombre}`}
                        disabled={quitar.isPending}
                        onClick={() => quitarUno(h)}
                      >
                        Quitar
                      </Button>
                    </div>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {dialogo}
    </div>
  );
}

function DialogoDeHecho({
  informeId,
  claves,
  hechos,
  borrador,
  onCerrar,
}: {
  informeId: number;
  claves: ClaveEditable[];
  hechos: Hecho[];
  borrador: BorradorDeHecho | null;
  onCerrar: () => void;
}) {
  return (
    <Dialog open={borrador !== null} onOpenChange={(abierto) => !abierto && onCerrar()}>
      <DialogContent className="sm:max-w-[30rem]">
        {/* La `key` reinicia el formulario con cada apertura: nada se arrastra del anterior. */}
        {borrador ? (
          <FormularioDeHecho
            key={`${borrador.fijo}:${borrador.clave}:${borrador.sujeto}`}
            informeId={informeId}
            claves={claves}
            hechos={hechos}
            inicial={borrador}
            onCerrar={onCerrar}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function FormularioDeHecho({
  informeId,
  claves,
  hechos,
  inicial,
  onCerrar,
}: {
  informeId: number;
  claves: ClaveEditable[];
  hechos: Hecho[];
  inicial: BorradorDeHecho;
  onCerrar: () => void;
}) {
  const escribir = useEscribirHecho(informeId);
  const opciones = clavesParaAgregar(claves, hechos);
  const [clave, setClave] = useState(inicial.clave || opciones[0]?.clave || "");
  const [sujeto, setSujeto] = useState(inicial.sujeto);
  const [valor, setValor] = useState(inicial.valor);
  const [errores, setErrores] = useState<Record<string, string>>({});

  const definicion = claves.find((c) => c.clave === clave);
  const pideSujeto = definicion !== undefined && definicion.sujeto !== "prohibido";

  function guardar() {
    escribir.mutate(
      { clave, sujeto: pideSujeto ? sujeto : "", valor },
      {
        onSuccess: () => {
          toast.success("Dato guardado.");
          onCerrar();
        },
        onError: (error) => {
          const delServidor = parseValidationErrors(error);
          if (delServidor) setErrores(delServidor);
          else toast.error(getApiErrorMessage(error, "No pudimos guardar el dato."));
        },
      },
    );
  }

  return (
    <form
      noValidate
      className="flex flex-col gap-4"
      onSubmit={(evento) => {
        evento.preventDefault();
        guardar();
      }}
    >
      <DialogHeader>
        <DialogTitle>{inicial.fijo ? `Editar ${definicion?.nombre ?? "dato"}` : "Agregar dato"}</DialogTitle>
        <DialogDescription>
          Lo que guardes queda confirmado y firmado con tu nombre. La IA no lo vuelve a pisar.
        </DialogDescription>
      </DialogHeader>

      {inicial.fijo ? null : (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="dato-de-la-hoja">Dato</Label>
          <NativeSelect
            id="dato-de-la-hoja"
            value={clave}
            aria-invalid={errores.clave !== undefined}
            onChange={(evento) => setClave(evento.target.value)}
          >
            {opciones.map((c) => (
              <NativeSelectOption key={c.clave} value={c.clave}>
                {c.nombre}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          {errores.clave ? <MensajeDeError>{errores.clave}</MensajeDeError> : null}
        </div>
      )}

      {pideSujeto ? (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="sujeto-del-dato">Sujeto</Label>
          {definicion.sujeto === "cerrado" ? (
            <NativeSelect
              id="sujeto-del-dato"
              value={sujeto}
              disabled={inicial.fijo}
              onChange={(evento) => setSujeto(evento.target.value)}
            >
              <NativeSelectOption value="">Elige uno</NativeSelectOption>
              {definicion.sujetos.map((s) => (
                <NativeSelectOption key={s} value={s}>
                  {s}
                </NativeSelectOption>
              ))}
            </NativeSelect>
          ) : (
            <Input
              id="sujeto-del-dato"
              value={sujeto}
              readOnly={inicial.fijo}
              aria-invalid={errores.sujeto !== undefined}
              onChange={(evento) => setSujeto(evento.target.value)}
              placeholder="Ej.: Cuartel 1"
            />
          )}
          {errores.sujeto ? <MensajeDeError>{errores.sujeto}</MensajeDeError> : null}
        </div>
      ) : null}

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="valor-del-dato">Valor</Label>
        <Input
          id="valor-del-dato"
          value={valor}
          aria-invalid={errores.valor !== undefined}
          onChange={(evento) => setValor(evento.target.value)}
        />
        {errores.valor ? <MensajeDeError>{errores.valor}</MensajeDeError> : null}
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCerrar}>
          Cancelar
        </Button>
        <Button type="submit" variant="fuerte" disabled={escribir.isPending || clave === ""}>
          Guardar dato
        </Button>
      </DialogFooter>
    </form>
  );
}

function MensajeDeError({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="text-label text-danger-soft-foreground">
      {children}
    </p>
  );
}
